import http from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import isEqual from 'hs-lodash/isEqual';
import quickFetch from 'quick-fetch';
import httpNoPortal from 'hub-http/clients/noPortalApiClient';
import { registerQuery, useQuery } from '../../utils/DataFetchingClient';
import { useSearchQuery } from 'academy-common-lib/__generated__/chirp/com/hubspot/userssearch/rpc/AppUsersSearch.hooks';
import { isPortalAccessDenied } from '../earlyPortalValidation/portalValidationConsts';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
export const GET_USER_INFO = registerQuery({
  fieldName: 'userInfo',
  args: [],
  fetcher() {
    const earlyMetaRequest = quickFetch.getRequestStateByName('user-info');
    const fetchUserInfo = () => httpNoPortal.get('/login-verify/user-info').then(response => response);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('user-info');
        });
        earlyMetaRequest.onError(() => {
          // retry the request
          // eslint-disable-next-line promise/catch-or-return
          fetchUserInfo().then(resolve, reject);
          quickFetch.removeEarlyRequest('user-info');
        });
      });
    }
    return fetchUserInfo();
  }
});
export const getUserInfo = client => {
  return client.queryUnwrapped({
    query: GET_USER_INFO
  });
};
export const useUserInfo = ({
  skip = false
} = {}) => {
  const {
    data: userInfo,
    loading: userInfoLoading,
    error: userInfoError
  } = useQuery(GET_USER_INFO, {
    skip
  });
  return {
    userInfo,
    userInfoLoading,
    userInfoError
  };
};
export const GET_ACCOUNTS = registerQuery({
  fieldName: 'accounts',
  args: [],
  fetcher() {
    return http.get('/accounts/v1/accounts').then(response => response);
  }
});
export const getAccounts = client => {
  return client.queryUnwrapped({
    query: GET_ACCOUNTS
  });
};
export const useAccounts = () => {
  const {
    data: accounts,
    loading: accountsLoading,
    error: accountsError
  } = useQuery(GET_ACCOUNTS);
  const sortedAccounts = useMemo(() => {
    if (!accounts) {
      return null;
    }
    return [...accounts.accounts].sort((a, b) => b.numberOfUsers - a.numberOfUsers);
  }, [accounts]);
  return {
    accounts: sortedAccounts,
    accountsLoading,
    accountsError
  };
};
export const useIsPaidAccount = () => {
  const {
    accounts,
    accountsLoading
  } = useAccounts();
  const isPaidAccount = useMemo(() => {
    if (!accounts) {
      return undefined;
    }
    const portalId = PortalIdParser.get();
    const portalAccount = accounts.find(acc => acc.id === portalId);
    if (!portalAccount) {
      return undefined;
    }
    return !isEqual(portalAccount.products, ['hubspot-free']);
  }, [accounts]);
  return {
    isPaidAccount,
    isPaidAccountLoading: accountsLoading
  };
};
export const GET_CROSS_ACCOUNTS = registerQuery({
  fieldName: 'crossAccounts',
  args: [],
  fetcher() {
    const earlyMetaRequest = quickFetch.getRequestStateByName('cross-hublet-accounts');
    const fetchCrossAccounts = () => httpNoPortal.get('/accounts/v1/accounts/cross-hublet').then(response => response);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('cross-hublet-accounts');
        });
        earlyMetaRequest.onError(() => {
          // retry the request
          // eslint-disable-next-line promise/catch-or-return
          fetchCrossAccounts().then(resolve, reject);
          quickFetch.removeEarlyRequest('cross-hublet-accounts');
        });
      });
    }
    return fetchCrossAccounts();
  }
});
export const getCrossAccounts = client => {
  return client.queryUnwrapped({
    query: GET_CROSS_ACCOUNTS
  });
};
export const useUsersForAccount = ({
  userIds,
  skip = false
}) => {
  var _userIds$length;
  const {
    data,
    loading: usersForAccountLoading,
    error: usersForAccountError
  } = useSearchQuery({
    variables: {
      mustMatchUserIds: userIds !== null && userIds !== void 0 ? userIds : [],
      offset: 0,
      pageLength: (_userIds$length = userIds === null || userIds === void 0 ? void 0 : userIds.length) !== null && _userIds$length !== void 0 ? _userIds$length : 0,
      onlyConfirmedUsers: false,
      onlyVisibleUsers: false,
      onlyHubspotters: false,
      uniqueUserIds: false,
      includePartnerUsers: true,
      // Resolve names for deactivated users too: this is an ID-lookup path for
      // historical records (audit log, "assigned by"), where a user who has
      // since left should still show their name, not a raw numeric ID.
      includeDeactivatedUsers: true
    },
    skip: skip || !(userIds !== null && userIds !== void 0 && userIds.length)
  });
  const usersForAccount = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return {
      users: data.results.map(user => ({
        id: user.userId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      }))
    };
  }, [data]);
  return {
    usersForAccount,
    usersForAccountLoading,
    usersForAccountError
  };
};
const toGatesAndScopes = result => {
  var _result$portal, _result$portal2, _result$user;
  return {
    gates: [...((result === null || result === void 0 || (_result$portal = result.portal) === null || _result$portal === void 0 ? void 0 : _result$portal.enabled_gates) || []), ...((result === null || result === void 0 || (_result$portal2 = result.portal) === null || _result$portal2 === void 0 ? void 0 : _result$portal2.enabled_v3_gates) || [])],
    scopes: (result === null || result === void 0 || (_result$user = result.user) === null || _result$user === void 0 ? void 0 : _result$user.scopes) || []
  };
};
const toUserLevelGatesAndScopes = result => ({
  gates: [...((result === null || result === void 0 ? void 0 : result.enabledGates) || []), ...((result === null || result === void 0 ? void 0 : result.enabledV3Gates) || [])],
  scopes: []
});
const EMPTY_GATES_AND_SCOPES = {
  gates: [],
  scopes: []
};
export const GET_GATES_AND_SCOPES = registerQuery({
  fieldName: 'gatesAndScopes',
  args: [],
  fetcher() {
    if (!PortalIdParser.get()) {
      return httpNoPortal.get('/login-verify/user-info').then(toUserLevelGatesAndScopes);
    }
    const earlyMetaRequest = quickFetch.getRequestStateByName('api-verify');
    const fetchGatesAndScopes = () => http.get('/login-verify/hub-user-info').then(toGatesAndScopes).catch(error => {
      // Resolving empty keeps each app's portal-resolution flow (hub
      // picker, hublet redirect) in charge of an inaccessible portal id
      // instead of the caller surfacing a content error. Everything else
      // still rejects so failure injection stays observable.
      if (isPortalAccessDenied(error === null || error === void 0 ? void 0 : error.status)) {
        return EMPTY_GATES_AND_SCOPES;
      }
      throw error;
    });
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(toGatesAndScopes(result));
          quickFetch.removeEarlyRequest('api-verify');
        });
        earlyMetaRequest.onError(xhr => {
          if (xhr.status === 401) {
            reject(xhr);
          } else if (isPortalAccessDenied(xhr.status)) {
            resolve(EMPTY_GATES_AND_SCOPES);
            quickFetch.removeEarlyRequest('api-verify');
          } else {
            void fetchGatesAndScopes().then(resolve, reject);
            quickFetch.removeEarlyRequest('api-verify');
          }
        });
      });
    }
    return fetchGatesAndScopes();
  }
});
export const getGatesAndScopes = client => {
  return client.queryUnwrapped({
    query: GET_GATES_AND_SCOPES
  });
};
export const useGatesAndScopes = () => {
  const {
    data: gatesAndScopes,
    loading: gatesAndScopesLoading,
    error: gatesAndScopesError
  } = useQuery(GET_GATES_AND_SCOPES);
  return {
    gatesAndScopes,
    gatesAndScopesLoading,
    gatesAndScopesError
  };
};
export const useGateOverrideStore = create()(persist(set => ({
  setOverride(key, value) {
    set({
      [key]: value
    });
  },
  resetOverride(key) {
    set({
      [key]: undefined
    });
  }
}), {
  name: 'academy:overrideGate'
}));
export const useGateOverride = gateName => {
  var _store$gateName;
  const store = useGateOverrideStore();
  const setOverride = useCallback(value => {
    store.setOverride(gateName, value);
  }, [gateName, store]);
  const resetOverride = useCallback(() => {
    store.resetOverride(gateName);
  }, [gateName, store]);
  return [(_store$gateName = store[gateName]) !== null && _store$gateName !== void 0 ? _store$gateName : null, setOverride, resetOverride];
};
export const useHasGate = gateName => {
  const {
    gatesAndScopes,
    gatesAndScopesError,
    gatesAndScopesLoading
  } = useGatesAndScopes();
  const [gateOverride] = useGateOverride(gateName);
  const hasGate = useMemo(() => {
    if (gateOverride !== null) return gateOverride;
    if (!gatesAndScopes) return null;
    const {
      gates
    } = gatesAndScopes;
    return gates.includes(gateName);
  }, [gateName, gateOverride, gatesAndScopes]);
  return {
    hasGate,
    hasGateError: gatesAndScopesError,
    hasGateLoading: gatesAndScopesLoading
  };
};
export const useScopeOverrideStore = create()(persist(set => ({
  setOverride(key, value) {
    set({
      [key]: value
    });
  },
  resetOverride(key) {
    set({
      [key]: undefined
    });
  }
}), {
  name: 'academy:overrideGate'
}));
export const useScopeOverride = scopeName => {
  var _store$scopeName;
  const store = useScopeOverrideStore();
  const setOverride = useCallback(value => {
    store.setOverride(scopeName, value);
  }, [scopeName, store]);
  const resetOverride = useCallback(() => {
    store.resetOverride(scopeName);
  }, [scopeName, store]);
  return [(_store$scopeName = store[scopeName]) !== null && _store$scopeName !== void 0 ? _store$scopeName : null, setOverride, resetOverride];
};
export const useHasScope = scopeName => {
  const {
    gatesAndScopes,
    gatesAndScopesLoading
  } = useGatesAndScopes();
  const [scopeOverride] = useScopeOverride(scopeName);
  const hasScope = useMemo(() => {
    if (scopeOverride !== null) return scopeOverride;
    if (!gatesAndScopes) return false;
    const {
      scopes
    } = gatesAndScopes;
    return scopes.includes(scopeName);
  }, [scopeName, scopeOverride, gatesAndScopes]);
  return {
    hasScope,
    gatesAndScopesLoading
  };
};
export const useScopes = () => {
  const {
    gatesAndScopes
  } = useGatesAndScopes();
  if (!gatesAndScopes) return null;
  const {
    scopes
  } = gatesAndScopes;
  return scopes;
};
const GET_PORTAL_SCOPES = registerQuery({
  fieldName: 'portalScopes',
  args: ['portalId'],
  fetcher({
    portalId
  }) {
    const earlyMetaRequest = quickFetch.getRequestStateByName('portal-scopes');
    const fetchPortalScopes = () => http.get(`/hubs2/v1/scopes/hub/${portalId}`);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('portal-scopes');
        });
        earlyMetaRequest.onError(() => {
          void fetchPortalScopes().then(resolve, reject);
          quickFetch.removeEarlyRequest('portal-scopes');
        });
      });
    }
    return fetchPortalScopes();
  }
});
export const useHasPortalScope = (portalId, scopeName) => {
  const {
    data: portalScopesResponse,
    loading: portalScopesLoading,
    error: portalScopesError
  } = useQuery(GET_PORTAL_SCOPES, {
    variables: {
      portalId: portalId
    },
    skip: !portalId
  });
  const hasPortalScope = useMemo(() => {
    var _portalScopesResponse;
    if (portalScopesLoading) return undefined;
    const portalScopes = (_portalScopesResponse = portalScopesResponse === null || portalScopesResponse === void 0 ? void 0 : portalScopesResponse.scopes) !== null && _portalScopesResponse !== void 0 ? _portalScopesResponse : [];
    if (!portalScopes.length) return false;
    return portalScopes.includes(scopeName);
  }, [portalScopesResponse, scopeName, portalScopesLoading]);
  return {
    hasPortalScope,
    portalScopesLoading,
    portalScopesError
  };
};